import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { fetchWithAuth } from "../utils/api";
import DiveManagerModal from "./DiveManagerModal";
import HamburgerMenu from "./HamburgerMenu";
import PageHeader from "./PageHeader";
import UserLink from "./UserLink";
import ConditionsModal from "./ConditionsModal";
import "./Profile.css";

export default function MyDives({ currentUser, onLogout }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [upcomingDives, setUpcomingDives] = useState([]);
  const [diveHistory, setDiveHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState(null);

  const [selectedDive, setSelectedDive] = useState(null);
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [showManagerModal, setShowManagerModal] = useState(false);
  const [conditionsFor, setConditionsFor] = useState(null); // { site, date } pour pré-remplir
  const [clubDives, setClubDives] = useState([]);

  const loadClubDives = useCallback(() => {
    fetchWithAuth(`${apiUrl}/my-club-dives`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setClubDives(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [apiUrl]);
  useEffect(() => { loadClubDives(); }, [loadClubDives]);

  const handleJoinClubDive = async (diveId) => {
    try {
      await fetchWithAuth(`${apiUrl}/join-dive`, { method: "POST", body: JSON.stringify({ dive_intention_id: diveId }) });
      loadClubDives();
    } catch (e) { console.error("join club dive", e); }
  };

  const refreshUpcomingDives = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const res = await fetchWithAuth(
        `${apiUrl}/profile/${currentUser.id}?email=${currentUser.email}`
      );
      const data = await res.json();
      if (Array.isArray(data)) setUpcomingDives(data);
      else if (data?.data && Array.isArray(data.data)) setUpcomingDives(data.data);
      else setUpcomingDives([]);
    } catch (e) {
      console.error("Erreur plongées à venir :", e);
    }
  }, [apiUrl, currentUser]);

  useEffect(() => {
    refreshUpcomingDives();
  }, [refreshUpcomingDives]);

  // Invitations en attente
  const [invitations, setInvitations] = useState([]);

  const refreshInvitations = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const res = await fetchWithAuth(`${apiUrl}/my-invitations`);
      const data = await res.json();
      setInvitations(Array.isArray(data) ? data : []);
      // Prévient la BottomNav pour rafraîchir sa pastille "Plongées".
      window.dispatchEvent(new Event("invitations-changed"));
    } catch (e) {
      console.error("Erreur invitations :", e);
    }
  }, [apiUrl, currentUser]);

  useEffect(() => {
    refreshInvitations();
  }, [refreshInvitations]);

  const handleAcceptInvitation = async (inv) => {
    try {
      const res = await fetchWithAuth(`${apiUrl}/join-dive`, {
        method: "POST",
        body: JSON.stringify({ dive_intention_id: inv.id }),
      });
      if (!res.ok) throw new Error("join failed");
      toast.success(t('map.toasts.diveJoined'));
      await refreshInvitations();
      await refreshUpcomingDives();
    } catch (e) {
      console.error(e);
      toast.error(t('map.toasts.joinError'));
    }
  };

  const handleDeclineInvitation = async (inv) => {
    try {
      const res = await fetchWithAuth(`${apiUrl}/decline-invitation`, {
        method: "POST",
        body: JSON.stringify({ dive_intention_id: inv.id }),
      });
      if (!res.ok) throw new Error("decline failed");
      toast.info(t('invitations.declined'));
      await refreshInvitations();
    } catch (e) {
      console.error(e);
      toast.error(t('map.toasts.joinError'));
    }
  };

  useEffect(() => {
    if (!currentUser?.id) return;
    let active = true;
    (async () => {
      try {
        setLoadingHistory(true);
        const res = await fetchWithAuth(`${apiUrl}/my-dive-history`);
        const data = await res.json();
        if (active) setDiveHistory(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Erreur historique :", e);
      } finally {
        if (active) setLoadingHistory(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [apiUrl, currentUser]);

  return (
    <div className="profile-container" style={{
      width: "90%", maxWidth: "900px", margin: "40px auto", padding: "20px",
      backgroundColor: "#f9f9f9", borderRadius: "12px",
      boxShadow: "0 4px 10px rgba(0,0,0,0.1)", position: "relative",
    }}>
      <div style={{ position: "absolute", top: "15px", right: "10px", zIndex: 1500 }}>
        <HamburgerMenu
          onLogout={onLogout}
          onProfileClick={() => navigate("/profile")}
          onAboutClick={() => navigate("/about")}
          onMapClick={() => navigate("/")}
          onCommunityClick={() => navigate("/community")}
        />
      </div>

      <div className="page-header-bleed">
        <PageHeader title={t('myDives.title')} />
      </div>

      {/* ---- Invitations en attente ---- */}
      {invitations.length > 0 && (
        <div style={{ background: "#fff8e6", border: "1px solid #ffe0a0", borderRadius: "10px", padding: "14px 16px", margin: "18px 0" }}>
          <h3 style={{ color: "#e67e00", margin: "0 0 10px", fontSize: "17px" }}>
            {t('invitations.title', { count: invitations.length })}
          </h3>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {invitations.map((inv) => (
              <li key={inv.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", padding: "10px 0", borderBottom: "1px solid #ffe9c2" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: "#333", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inv.site_name || "—"}</div>
                  <div style={{ fontSize: "13px", color: "#777" }}>
                    {inv.planned_date}{inv.planned_time ? ` · ${inv.planned_time.slice(0, 5)}` : ""}
                  </div>
                  <div style={{ fontSize: "12px", color: "#999" }}>
                    {t('invitations.by')} <UserLink id={inv.user_id || inv.user?.id} firstName={inv.user?.first_name} lastName={inv.user?.last_name} fallback="?" />
                  </div>
                </div>
                <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                  <button onClick={() => handleAcceptInvitation(inv)} style={{ background: "var(--primary-blue)", color: "#fff", border: "none", borderRadius: "6px", padding: "7px 12px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
                    {t('invitations.acceptShort')}
                  </button>
                  <button onClick={() => handleDeclineInvitation(inv)} title={t('invitations.decline')} style={{ background: "#eee", color: "#666", border: "none", borderRadius: "6px", padding: "7px 11px", fontSize: "14px", cursor: "pointer" }}>
                    ✕
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ---- Plongées à venir ---- */}
      <h3 style={{ color: "var(--primary-blue)", margin: "18px 0 10px" }}>
        {t('myDives.upcoming')}
      </h3>

      {upcomingDives.length === 0 ? (
        <div style={{
          background: "#fff", borderRadius: "10px", padding: "26px 18px", textAlign: "center",
          boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
        }}>
          <p style={{ color: "#555", margin: "0 0 14px" }}>{t('myDives.emptyUpcoming')}</p>
          <button
            onClick={() => navigate("/?create=1")}
            style={{
              background: "var(--primary-blue)", color: "#fff", border: "none",
              borderRadius: "8px", padding: "10px 18px", fontWeight: 700, cursor: "pointer",
            }}
          >
            + {t('create.button')}
          </button>
        </div>
      ) : (
        <ul style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 300px))",
          justifyContent: "flex-start", gap: "20px", listStyle: "none", padding: 0,
          margin: "10px auto", maxWidth: "1200px", width: "100%",
        }}>
          {[...upcomingDives]
            .sort((a, b) => new Date(a.planned_date) - new Date(b.planned_date))
            .map((sub) => {
              const isOrg = sub.user_id === currentUser.id;
              const dive = sub.dive_intention || sub;
              const siteName = dive.site?.name || "—";
              return (
                <li key={dive.id || sub.id} style={{
                  backgroundColor: "#fff", padding: "14px 16px", borderRadius: "10px",
                  boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                  borderLeft: isOrg ? "4px solid var(--primary-blue)" : "4px solid #48cae4",
                  display: "flex", flexDirection: "column", justifyContent: "space-between",
                }}>
                  <div>
                    <p style={{ margin: "0 0 4px" }}><strong>{t('profile.diveCard.site')}</strong> {siteName}</p>
                    <p style={{ margin: "0 0 4px" }}><strong>{t('profile.diveCard.date')}</strong> {dive.planned_date}</p>
                    <p style={{ margin: "0 0 4px" }}><strong>{t('profile.diveCard.time')}</strong> {dive.planned_time}</p>
                    <p style={{ margin: "0 0 6px" }}><strong>{t('profile.diveCard.type')}</strong> {dive.dive_type}</p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedDive(dive);
                      setIsOrganizer(isOrg);
                      setShowManagerModal(true);
                    }}
                    style={{
                      width: "100%", background: "var(--primary-blue)", color: "white", border: "none",
                      borderRadius: "6px", padding: "8px", cursor: "pointer", fontSize: "13px", marginTop: "8px",
                    }}
                  >
                    {t('profile.buttons.manageDive')}
                  </button>
                </li>
              );
            })}
        </ul>
      )}

      {clubDives.length > 0 && (
        <>
          <hr style={{ margin: "22px 0", borderColor: "#ddd" }} />
          <h3 style={{ color: "var(--primary-blue)", marginBottom: "10px" }}>{t('clubs.myClubDivesTitle')}</h3>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {clubDives.map((d) => (
              <li key={d.id} style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 600, color: "#223344" }}>{d.site?.name || "?"}</span>
                  {d.club?.name && <span style={{ fontSize: 12, color: "#e0a400", fontWeight: 700, background: "#fff6e0", borderRadius: 999, padding: "2px 8px" }}>{d.club.name}</span>}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 2 }}>
                  <span style={{ fontSize: 13, color: "#667" }}>
                    {new Date(d.planned_date).toLocaleDateString(i18n.language, { weekday: "short", day: "numeric", month: "short" })}
                    {d.planned_time ? ` · ${d.planned_time.slice(0, 5)}` : ""}
                    {d.organizer?.first_name ? <> · <UserLink id={d.organizer.id} firstName={d.organizer.first_name} /></> : ""}
                  </span>
                  {d.is_organizer ? (
                    <span style={{ fontSize: 12, color: "#8a97a3", fontWeight: 700, flexShrink: 0 }}>{t('clubs.organizerTag')}</span>
                  ) : d.is_participant ? (
                    <span style={{ fontSize: 12, color: "#2e9e5b", fontWeight: 700, flexShrink: 0 }}>{t('clubs.joinedTag')}</span>
                  ) : (
                    <button onClick={() => handleJoinClubDive(d.id)} style={{ background: "var(--primary-blue)", color: "#fff", border: "none", borderRadius: 8, padding: "5px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
                      {t('map.buttons.join')}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      <hr style={{ margin: "22px 0", borderColor: "#ddd" }} />

      {/* ---- Historique ---- */}
      <h3 style={{ color: "var(--primary-blue)", marginBottom: "10px" }}>{t('profile.history.title')}</h3>
      <div style={{ background: "#fff", padding: "15px 20px", borderRadius: "8px", boxShadow: "0 2px 5px rgba(0,0,0,0.05)" }}>
        {loadingHistory ? (
          <p style={{ color: "#888", textAlign: "center", margin: "10px 0" }}>{t('profile.history.loading')}</p>
        ) : diveHistory.length > 0 ? (
          <>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {(showAllHistory ? diveHistory : diveHistory.slice(0, 5)).map((dive, index) => {
                const id = dive.id || index;
                const open = expandedHistory === id;
                const siteName = dive.site?.name || "—";
                const allBuddies = [...(dive.participants || [])];
                if (dive.organizer && !allBuddies.some((p) => p.id === dive.organizer.id)) allBuddies.push(dive.organizer);
                const buddiesExceptMe = allBuddies.filter((p) => p.id !== currentUser?.id);
                const buddyNames = buddiesExceptMe.length > 0 ? buddiesExceptMe.map((p) => p.first_name).join(", ") : "—";
                return (
                  <li key={id} style={{ borderBottom: "1px solid #eee" }}>
                    <button
                      onClick={() => setExpandedHistory(open ? null : id)}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", background: "none", border: "none", padding: "11px 4px", cursor: "pointer", textAlign: "left", fontSize: "14px", color: "#223344" }}
                    >
                      <span style={{ fontWeight: 600, whiteSpace: "nowrap", color: "#555" }}>
                        {new Date(dive.planned_date).toLocaleDateString(i18n.language, { day: "2-digit", month: "2-digit", year: "2-digit" })}
                      </span>
                      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{siteName}</span>
                      <span style={{ color: "#8aa0a8", fontSize: "12px", flexShrink: 0 }}>{open ? "▲" : "▼"}</span>
                    </button>
                    {open && (
                      <div style={{ padding: "0 6px 12px", fontSize: "13px", color: "#555", lineHeight: 1.6 }}>
                        <div><strong>{t('profile.diveCard.type')}</strong> {dive.dive_type || "—"}</div>
                        <div><strong>{t('profile.history.participants')}</strong> {buddyNames}</div>
                        {dive.site?.id && (
                          <button
                            onClick={() => setConditionsFor({
                              site: { id: dive.site.id, name: siteName },
                              date: (dive.planned_date || "").slice(0, 10),
                            })}
                            style={{
                              marginTop: 8,
                              background: "rgba(0,119,182,0.10)",
                              color: "var(--primary-blue)",
                              border: "1px solid rgba(0,119,182,0.25)",
                              borderRadius: 999,
                              padding: "6px 12px",
                              fontSize: "12px",
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >
                            + {t('conditions.reportButton')}
                          </button>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            {diveHistory.length > 5 && (
              <div style={{ textAlign: "center", marginTop: "12px" }}>
                <button onClick={() => setShowAllHistory(!showAllHistory)} style={{
                  background: showAllHistory ? "#e0e0e0" : "var(--primary-blue)", color: showAllHistory ? "#333" : "white",
                  border: "none", borderRadius: "6px", padding: "8px 16px", cursor: "pointer", fontWeight: "500", fontSize: "13px",
                }}>
                  {showAllHistory ? t('profile.history.showLess') : t('profile.history.showAll', { count: diveHistory.length })}
                </button>
              </div>
            )}
          </>
        ) : (
          <p style={{ color: "#888", textAlign: "center", margin: "10px 0" }}>{t('profile.history.noHistory')}</p>
        )}
      </div>

      <div className="profile-bottom-spacer" />

      {showManagerModal && selectedDive && (
        <DiveManagerModal
          dive={selectedDive}
          isOrganizer={isOrganizer}
          currentUser={currentUser}
          onClose={() => setShowManagerModal(false)}
          onUpdated={refreshUpcomingDives}
        />
      )}

      {conditionsFor && (
        <ConditionsModal
          apiUrl={apiUrl}
          initialSite={conditionsFor.site}
          initialDate={conditionsFor.date}
          onClose={() => setConditionsFor(null)}
        />
      )}

      <ToastContainer position="bottom-center" autoClose={3000} theme="colored" />
    </div>
  );
}
